import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { initAnalytics } from '../../../analytics.config';
import useAppUser from '../../../hooks/useAppUser';
import { queryClient } from '../../../queryClient';
import {
  recordLegalAcceptance,
  setMarketingConsent,
  updateConsentPreferences,
} from '../consentService';
import {
  createEmptyConsentSelection,
  getConsentErrorMessages,
  needsReacceptance,
  type ConsentSelection,
} from '../legalConfig';
import { getAnalyticsConsentStatus, setAnalyticsConsentStatus } from '../legalStorage';
import TermsConsentField from '../components/TermsConsentField';
import OptionalConsentsSection from '../components/OptionalConsentsSection';
import { useLegalConsentSummary } from '../useLegalConsentSummary';
import AppLoadingScreen from '../../../components/AppLoadingScreen';

export default function ConsentSettingsPage() {
  const navigate = useNavigate();
  const { firebaseUser, loading } = useAppUser();
  const { data: summary, isLoading } = useLegalConsentSummary();
  const [legalSelection, setLegalSelection] = useState<ConsentSelection>(createEmptyConsentSelection());
  const [marketingEnabledOverride, setMarketingEnabledOverride] = useState<boolean | null>(null);
  const [analyticsEnabledOverride, setAnalyticsEnabledOverride] = useState<boolean | null>(null);
  const [legalSubmitting, setLegalSubmitting] = useState(false);
  const [marketingSubmitting, setMarketingSubmitting] = useState(false);
  const [analyticsSubmitting, setAnalyticsSubmitting] = useState(false);
  const [legalMessage, setLegalMessage] = useState<string | null>(null);
  const [marketingMessage, setMarketingMessage] = useState<string | null>(null);
  const [analyticsMessage, setAnalyticsMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !firebaseUser) {
      navigate('/login', { replace: true, state: { from: { pathname: '/mes-consentements' } } });
    }
  }, [firebaseUser, loading, navigate]);

  const marketingEnabled = marketingEnabledOverride ?? Boolean(summary?.marketingEmailAccepted);
  const analyticsEnabled = analyticsEnabledOverride
    ?? (getAnalyticsConsentStatus() === 'accepted' || Boolean(summary?.analyticsCookiesAccepted));

  const requiresFullReacceptance = needsReacceptance(summary);

  const marketingStatusLabel = useMemo(() => {
    if (summary?.marketingEmailAccepted === true) {
      return 'Activé';
    }

    if (summary?.marketingEmailAccepted === false) {
      return 'Désactivé';
    }

    return 'Non renseigné';
  }, [summary?.marketingEmailAccepted]);

  const analyticsStatusLabel = useMemo(() => {
    if (summary?.analyticsCookiesAccepted === true || getAnalyticsConsentStatus() === 'accepted') {
      return 'Activé';
    }

    if (summary?.analyticsCookiesAccepted === false || getAnalyticsConsentStatus() === 'rejected') {
      return 'Désactivé';
    }

    return 'Non renseigné';
  }, [summary?.analyticsCookiesAccepted]);

  const handleLegalSubmit = async () => {
    setLegalSubmitting(true);
    setLegalMessage(null);

    try {
      const nextSelection = {
        ...createEmptyConsentSelection(),
        ...legalSelection,
        marketingEmailAccepted: marketingEnabled,
        analyticsCookiesAccepted: analyticsEnabled,
      };

      const messages = getConsentErrorMessages(nextSelection);
      if (messages.length > 0) {
        setLegalMessage(messages[0]);
        return;
      }

      await recordLegalAcceptance({
        source: 'profile',
        email: firebaseUser?.email,
        selection: nextSelection,
      });

      setLegalMessage('La version en vigueur des documents légaux a bien été enregistrée.');

      if (analyticsEnabled) {
        setAnalyticsConsentStatus('accepted');
        await initAnalytics();
      } else {
        setAnalyticsConsentStatus('rejected');
      }

      await queryClient.invalidateQueries({ queryKey: ['legal-consent-summary', firebaseUser?.uid ?? null] });
    } catch (error) {
      console.error('Erreur lors de la reacceptation legale:', error);
      setLegalMessage('Impossible d enregistrer la réacceptation pour le moment.');
    } finally {
      setLegalSubmitting(false);
    }
  };

  const handleMarketingSubmit = async () => {
    if (!firebaseUser?.email) {
      setMarketingMessage('Aucune adresse email de compte n est disponible pour enregistrer ce choix.');
      return;
    }

    setMarketingSubmitting(true);
    setMarketingMessage(null);

    try {
      await setMarketingConsent({
        email: firebaseUser.email,
        granted: marketingEnabled,
        source: 'profile',
        locale: typeof navigator !== 'undefined' ? navigator.language : null,
      });

      setMarketingMessage(
        marketingEnabled
          ? 'Votre consentement newsletter est désormais activé.'
          : 'Votre consentement newsletter est désormais désactivé.'
      );

      await queryClient.invalidateQueries({ queryKey: ['legal-consent-summary', firebaseUser.uid] });
    } catch (error) {
      console.error('Erreur lors de la mise a jour du consentement marketing:', error);
      setMarketingMessage('Impossible d enregistrer le choix newsletter pour le moment.');
    } finally {
      setMarketingSubmitting(false);
    }
  };

  const handleAnalyticsSubmit = async () => {
    setAnalyticsSubmitting(true);
    setAnalyticsMessage(null);

    try {
      await updateConsentPreferences({
        analyticsCookiesAccepted: analyticsEnabled,
      });

      if (analyticsEnabled) {
        setAnalyticsConsentStatus('accepted');
        await initAnalytics();
      } else {
        setAnalyticsConsentStatus('rejected');
      }

      setAnalyticsMessage(
        analyticsEnabled
          ? 'La mesure d audience optionnelle est activée.'
          : 'La mesure d audience optionnelle est désactivée.'
      );

      await queryClient.invalidateQueries({ queryKey: ['legal-consent-summary', firebaseUser?.uid ?? null] });
    } catch (error) {
      console.error('Erreur lors de la mise a jour du consentement analytics:', error);
      setAnalyticsMessage('Impossible d enregistrer le choix analytics pour le moment.');
    } finally {
      setAnalyticsSubmitting(false);
    }
  };

  if (loading) {
    return <AppLoadingScreen message="Verification de votre profil..." minHeight="60vh" />;
  }

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ pt: 16, pb: 8 }}>
        <Alert severity="info" variant="outlined">
          Chargement de vos consentements en cours...
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ pt: 16, pb: 8 }}>
      <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 4 }}>
        <Stack spacing={3}>
          <div>
            <Typography variant="h3" gutterBottom>
              Mes consentements
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Cette page sépare la réacceptation des documents légaux, la newsletter marketing et la mesure d audience optionnelle.
            </Typography>
          </div>

          {summary?.acceptedAt ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Dernière acceptation légale: ${summary.acceptedAt}`} />
              <Chip label={`CGV ${summary.termsVersion || 'non enregistree'}`} />
              <Chip label={`Confidentialite ${summary.privacyVersion || 'non enregistree'}`} />
            </Stack>
          ) : (
            <Alert severity="warning" variant="outlined">
              Aucune preuve d acceptation versionnée n est encore rattachée à votre compte.
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
            <Stack spacing={2.5}>
              <Typography variant="h5">Documents légaux</Typography>
              {requiresFullReacceptance ? (
                <Alert severity="warning" variant="outlined">
                  Une réacceptation complète est nécessaire car les versions en vigueur ont changé ou n ont jamais été enregistrées pour ce compte.
                </Alert>
              ) : (
                <Alert severity="success" variant="outlined">
                  Les versions légales actuellement enregistrées pour ce compte sont à jour.
                </Alert>
              )}

              {requiresFullReacceptance ? (
                <TermsConsentField
                  value={legalSelection}
                  onChange={setLegalSelection}
                  errorMessages={legalMessage ? [legalMessage] : []}
                />
              ) : null}

              {!requiresFullReacceptance && legalMessage ? (
                <Alert severity="success" variant="outlined">
                  {legalMessage}
                </Alert>
              ) : null}

              {requiresFullReacceptance ? (
                <Button variant="contained" onClick={() => void handleLegalSubmit()} disabled={legalSubmitting}>
                  {legalSubmitting ? 'Enregistrement...' : 'Réaccepter les documents légaux'}
                </Button>
              ) : null}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                <div>
                  <Typography variant="h5">Newsletter marketing</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ce choix est indépendant des CGV et de la politique de confidentialité. Il ne sert qu'à recevoir des emails d'actualité et d'offres Jacana Nature.
                  </Typography>
                </div>
                <Chip label={`État actuel: ${marketingStatusLabel}`} color={summary?.marketingEmailAccepted ? 'success' : 'default'} />
              </Stack>

              <Typography variant="body2" color="text.secondary">
                Dernière mise à jour backend: {summary?.marketingConsentUpdatedAt || 'non enregistrée'}
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={marketingEnabled}
                    onChange={(event) => setMarketingEnabledOverride(event.target.checked)}
                  />
                }
                label="J'accepte de recevoir la newsletter et les emails marketing Jacana Nature."
              />

              {marketingMessage ? (
                <Alert severity={marketingMessage.includes('Impossible') ? 'error' : 'success'} variant="outlined">
                  {marketingMessage}
                </Alert>
              ) : null}

              <Button variant="contained" onClick={() => void handleMarketingSubmit()} disabled={marketingSubmitting}>
                {marketingSubmitting ? 'Enregistrement...' : 'Enregistrer mon choix newsletter'}
              </Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                <div>
                  <Typography variant="h5">Mesure d audience optionnelle</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cette préférence pilote Firebase Analytics. Elle est distincte de la newsletter et des documents légaux.
                  </Typography>
                </div>
                <Chip label={`État actuel: ${analyticsStatusLabel}`} color={analyticsEnabled ? 'success' : 'default'} />
              </Stack>

              <Typography variant="body2" color="text.secondary">
                Dernière mise à jour backend: {summary?.analyticsConsentUpdatedAt || 'non enregistrée'}
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={analyticsEnabled}
                    onChange={(event) => setAnalyticsEnabledOverride(event.target.checked)}
                  />
                }
                label="J'autorise la mesure d'audience pour améliorer le site."
              />

              {analyticsMessage ? (
                <Alert severity={analyticsMessage.includes('Impossible') ? 'error' : 'success'} variant="outlined">
                  {analyticsMessage}
                </Alert>
              ) : null}

              <Button variant="contained" onClick={() => void handleAnalyticsSubmit()} disabled={analyticsSubmitting}>
                {analyticsSubmitting ? 'Enregistrement...' : 'Enregistrer mon choix analytics'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Paper>
    </Container>
  );
}